/**
 * DeliveryScanQrCard (Jul-2026, User-Wunsch) — QR-Karte für den mobilen
 * Wareneingang. Der Desktop erzeugt eine Session, der QR öffnet am Handy die
 * Wareneingang-Seite — MIT Login (User-Wunsch Jul-2026): der Mitarbeiter
 * meldet sich am Handy an (Lieferschein fotografieren → KI → direkt buchen).
 * Nov-2026 (User-Wunsch): Session wird sofort beim Laden erzeugt (kein
 * Endlos-Spinner mehr); Doppelklick auf den QR startet den Kamera-Scan.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Printer, X, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";

export default function DeliveryScanQrCard({ onMobileScan }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [sess, setSess] = useState(null);
    const [creating, setCreating] = useState(false);
    const sessRef = useRef(null);
    const inflightRef = useRef(null);

    const ensureSession = useCallback(async (silent = false) => {
        if (sessRef.current) return sessRef.current;
        if (inflightRef.current) return inflightRef.current;
        setCreating(true);
        const p = (async () => {
            try {
                const { data } = await api.post("/articles/goods-receipt-scan-sessions");
                sessRef.current = data;
                setSess(data);
                return data;
            } catch (e) {
                if (!silent) toast.error(e.response?.data?.detail || "QR-Session fehlgeschlagen");
                return null;
            } finally { setCreating(false); inflightRef.current = null; }
        })();
        inflightRef.current = p;
        return p;
    }, []);

    // Nov-2026 (User-Wunsch, Fix Endlos-Spinner): Session direkt beim Laden
    // erzeugen — der QR-Code ist sofort sichtbar (standortspezifischer Token).
    useEffect(() => { ensureSession(true); }, [ensureSession]);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next) await ensureSession();
    };

    const handlePrint = async () => {
        const s = await ensureSession();
        if (!s) return;
        const ok = await openQrPrint({
            title: "Wareneingang per Handy",
            subtitle: "Mit der Handy-Kamera scannen — öffnet den Wareneingang (Anmeldung erforderlich; Lieferschein fotografieren, KI erkennt Positionen, direkt buchen).",
            scanUrl: s.portal_url,
            ...dealerPrintInfo(user),
        });
        if (!ok) toast.error("Popup blockiert — bitte Pop-ups erlauben");
    };

    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="delivery-scan-qr">
            <div className="relative">
                <button
                    type="button"
                    onClick={toggle}
                    onDoubleClick={() => { setOpen(false); onMobileScan?.(); }}
                    title="Mit dem Handy abscannen → Wareneingang am Handy starten (Klick: vergrößern & drucken · Doppelklick: hier direkt mit Kamera scannen)"
                    className="bg-white rounded-md p-2.5 hover:ring-2 hover:ring-[#FFCC00] transition-shadow shadow-lg min-w-[116px] min-h-[116px] flex items-center justify-center"
                    data-testid="delivery-scan-qr-trigger"
                >
                    {sess ? <QRCodeSVG value={sess.portal_url} size={96} level="M" />
                        : creating ? <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            : <span className="text-[10px] font-bold text-gray-500 underline" data-testid="delivery-scan-qr-retry">QR laden</span>}
                </button>
                {open && (
                    <div className="absolute right-0 top-[132px] z-50 w-72 bg-white border border-gray-200 rounded-sm shadow-xl p-4 text-[#111827]" data-testid="delivery-scan-qr-panel">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Wareneingang per Handy</span>
                            <button type="button" onClick={() => setOpen(false)} data-testid="delivery-scan-qr-close"><X className="w-4 h-4 text-gray-500" /></button>
                        </div>
                        <div className="flex justify-center bg-white p-2 rounded-sm border-2 border-[#FFCC00] min-h-[196px] items-center">
                            {creating && !sess ? <Loader2 className="w-8 h-8 text-gray-400 animate-spin" /> : sess ? <QRCodeSVG value={sess.portal_url} size={180} level="M" /> : null}
                        </div>
                        <p className="text-[11px] text-gray-600 mt-2">Mit dem Handy scannen: öffnet den Wareneingang <b>mit Anmeldung</b> (einmal am Handy einloggen) — Lieferschein fotografieren, KI erkennt Positionen, direkt ins Lager buchen.</p>
                        <button type="button" onClick={handlePrint}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                            data-testid="delivery-scan-qr-print">
                            <Printer className="w-4 h-4" /> QR-Code drucken
                        </button>
                    </div>
                )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Mit Handy scannen · Doppelklick: Kamera
            </span>
        </div>
    );
}
